import React, { useState, MutableRefObject, useRef, useMemo, useCallback } from 'react';
import { IEditorProps } from './Editor';
import { IconButton } from '../buttons/IconButton';
import { DropDownOverlay } from '../inputs/DropDownOverlay';
import { IPropertyChild, getChildValueAsArray } from 'components/property-editors/utils/ProviderNodeUtils';
import { getEditorConfigValue } from 'components/property-editors/utils/EditorConfigurationUtils';
import { DropDownOption } from 'components/inputs/DropDownOption';
import './TagFilterEditor.less';
import { EditorLabel } from './EditorLabel';
import { useEditorId } from './hooks/useEditorId';

interface IGroupedListEditorProps extends IEditorProps {}

export function TagFilterEditor({ child, path, onChange }: IGroupedListEditorProps) {
  const blockClassName = 'dpc-tag-filter-editor';
  const [searchString, setSearchString] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef: MutableRefObject<HTMLDivElement | null> = useRef(null);
  const allOptions = useMemo(() => getOptions(child), [child]);
  const [selectedTags, setSelectedTags] = useState<string[]>(getChildValueAsArray(child));
  const availableOptions = allOptions.filter(o => !selectedTags.find(s => s === o));
  const searchedOptions = availableOptions.filter(o => o.toLowerCase().indexOf(searchString.toLowerCase()) > -1);
  const currentOptions = searchString ? searchedOptions : availableOptions;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [id] = useEditorId(path);

  const handleTagChosen = (id: string, name: any) => {
    const updatedTags = [...selectedTags, name];
    setSelectedTags(updatedTags);
    onChange(updatedTags, path);
    setIsOpen(false);
  };
  const handleClearTag = (name: string) => {
    const updatedTags = selectedTags.filter(s => s !== name);
    setSelectedTags(updatedTags);
    onChange(updatedTags, path);
    setIsOpen(false);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchString(value);
    setIsOpen(value ? true : false);
  };

  const resetSearch = () => {
    setSearchString('');
    setIsOpen(false);
    setCurrentIndex(0);
  };

  const handleComboIconClick = useCallback(() => {
    setSearchString('');
    setIsOpen(preState => !preState);
  }, []);

  const handleKeysOnSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!searchString || !currentOptions.length) return;
    switch (e.key) {
      case 'Enter':
        const currentTag = currentOptions[currentIndex];
        handleTagChosen('', currentTag);
        resetSearch();
        break;
      case 'ArrowUp' /* ↑ */:
        if (currentIndex > 0) setCurrentIndex(preIndex => preIndex - 1);
        else if (currentIndex === 0) setCurrentIndex(currentOptions.length - 1); //select the last item
        break;
      case 'ArrowDown' /* ↓ */:
        if (currentIndex < currentOptions.length - 1) setCurrentIndex(preIndex => preIndex + 1);
        else if (currentIndex === currentOptions.length - 1) setCurrentIndex(0); //select the first item
        break;

      case 'Escape':
        resetSearch();
        break;
    }
  };

  return (
    <EditorLabel htmlFor={id} editor={child} path={path} doNotWrap>
      <div className={`${blockClassName}`} ref={wrapperRef}>
        <div className={`${blockClassName}__holder`}>
          <div className={`${blockClassName}__selected`}>
            {selectedTags &&
              selectedTags.map(val => {
                return (
                  <div key={val} className={`${blockClassName}__selected-tag-holder`}>
                    <span className={`${blockClassName}__selected-tag`}>{val}</span>
                    <IconButton
                      className={`${blockClassName}__clear-tag dpc-combo-box__clear`}
                      onClick={() => {
                        handleClearTag(val);
                      }}
                      title="Remove"
                      iconClassName="dpc-combo-box__clear-icon"
                      iconSize="input"
                    />
                  </div>
                );
              })}
          </div>
          <input
            autoComplete="off"
            type="text"
            placeholder="Filter By Tags"
            className={`${blockClassName}__input`}
            value={searchString}
            onChange={handleSearch}
            onKeyUp={handleKeysOnSearch}
          />
        </div>
        <IconButton
          className="dpc-combo-box__arrow-down"
          onClick={handleComboIconClick}
          title="Open"
          iconClassName="dpc-combo-box__arrow-down-icon"
          iconSize="input"
        />
        <DropDownOverlay
          sourceRef={wrapperRef}
          isOpen={isOpen}
          onClose={() => {
            setIsOpen(false);
          }}
        >
          {currentOptions &&
            currentOptions.map((value: string, i: number) => (
              <DropDownOption
                key={i}
                index={i}
                onChosen={handleTagChosen}
                isChosen={currentIndex === i}
                searchString={searchString}
              >
                {value}
              </DropDownOption>
            ))}
        </DropDownOverlay>
      </div>
    </EditorLabel>
  );
}

function getOptions(child: IPropertyChild): string[] {
  return (getEditorConfigValue(child, 'values') || []).filter((value?: string | null) => {
    if (!value) {
      console.log('Improper Configuration: falsy value passed as Checklist entry name');
      return false;
    }
    return true;
  });
}
